import "./App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Outlet, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import Navbar from "./components/Navbar";
import SsoRecoveryBanner from "./components/SsoRecoveryBanner";
import LocationBar from "./components/LocationBar";
import Footer from "./components/Footer";
import ConsentRePrompt from "./components/ConsentRePrompt";
import WikipediaCard from "./components/WikipediaCard";
import AskTravelPulse from "./components/AskTravelPulse";
import ProtectedRoute from "./components/ProtectedRoute";
import { MobileBottomNav, InstallPrompt } from "./components/MobileShell";
import SocialPulseRibbon from "./components/SocialPulseRibbon";

import Home from "./pages/Home";
import CategoryHub from "./pages/CategoryHub";
import Rail from "./pages/Rail";
import RailStation from "./pages/RailStation";
import RailOperator from "./pages/RailOperator";
import TransportVertical from "./pages/TransportVertical";
import { TransportHubPage, TransportOperatorPage } from "./pages/TransportDetail";
import VisasHub from "./pages/VisasHub";
import VisaCountryDetail from "./pages/VisaCountryDetail";
import MissionProfile from "./pages/MissionProfile";
import RegionMissions from "./pages/RegionMissions";
import About from "./pages/About";
import AboutFounder from "./pages/AboutFounder";
import AffiliateDisclosure from "./pages/AffiliateDisclosure";
import SafeVault from "./pages/SafeVault";
import PrivateVault from "./pages/PrivateVault";
import PublicEmergencyCard from "./pages/PublicEmergencyCard";
import OurStory from "./pages/OurStory";
import SectionGuide from "./pages/SectionGuide";
import GuidesIndex from "./pages/GuidesIndex";
import ArticlesIndex from "./pages/ArticlesIndex";
import Marketplace from "./pages/Marketplace";
import BusinessDetail from "./pages/BusinessDetail";
import MyBusiness from "./pages/MyBusiness";
import ArticleView from "./pages/ArticleView";
import Destinations from "./pages/Destinations";
import DestinationDetail from "./pages/DestinationDetail";
import Airports from "./pages/Airports";
import AirportsDirectory from "./pages/AirportsDirectory";
import AirportDetail from "./pages/AirportDetail";
import Airlines from "./pages/Airlines";
import AirlinesDirectory from "./pages/AirlinesDirectory";
import GlobalAirlineRanking from "./pages/GlobalAirlineRanking";
import GlobalAirportRanking from "./pages/GlobalAirportRanking";
import AirlineDetail from "./pages/AirlineDetail";
import Countries from "./pages/Countries";
import CountryDetail from "./pages/CountryDetail";
import StateDetail from "./pages/StateDetail";
import Legal from "./pages/Legal";
import City from "./pages/City";
import TravelHub from "./pages/TravelHub";
import TripPlannerAI from "./pages/TripPlannerAI";
import TripToday from "./pages/TripToday";
import GetApp from "./pages/GetApp";
import IndiaAirports from "./pages/IndiaAirports";
import IndiaAirportsHome from "./pages/IndiaAirportsHome";
import IALayout from "./components/india/IAShell";
import IndiaAirportDetail from "./pages/IndiaAirportDetail";
import IndiaAirlines from "./pages/IndiaAirlines";
import IndiaAirlineDetail from "./pages/IndiaAirlineDetail";
import IndiaRoutePage from "./pages/IndiaRoutePage";
import IndiaStatePage from "./pages/IndiaStatePage";
import IndiaAbout from "./pages/IndiaAbout";
import IndiaMyFlight from "./pages/IndiaMyFlight";
import IndiaOciGuide from "./pages/IndiaOciGuide";
import IndiaVisitingGuide from "./pages/IndiaVisitingGuide";
import IndiaGuidesIndex from "./pages/IndiaGuidesIndex";
import IndiaPartners from "./pages/IndiaPartners";
import IndiaPlannerWidget from "./pages/IndiaPlannerWidget";
import RemindMe from "./pages/RemindMe";
import GearInventory from "./pages/GearInventory";
import TripDetail from "./pages/TripDetail";
import TripJourney from "./pages/TripJourney";
import FollowPage from "./pages/FollowPage";
import PublicBag from "./pages/PublicBag";
import TravelGroups, { GroupDetail, GroupJoin } from "./pages/TravelGroups";
import GroupGuardian from "./pages/GroupGuardian";
import SharedGuardian from "./pages/SharedGuardian";
import TripDiary from "./pages/TripDiary";
import ExpenseManager from "./pages/ExpenseManager";
import StoryWizard from "./pages/StoryWizard";
import StoryStudio from "./pages/StoryStudio";
import MyStories from "./pages/MyStories";
import PublicStory from "./pages/PublicStory";
import OurCochin from "./pages/OurCochin";
import TheChenda from "./pages/TheChenda";
import KeralaAyurveda from "./pages/KeralaAyurveda";
import Specials from "./pages/Specials";
import PhotoMap from "./pages/PhotoMap";
import MyPhotos from "./pages/MyPhotos";
import TravelDay from "./pages/TravelDay";
import TripShareView from "./pages/TripShareView";
import Memories from "./pages/Memories";
import Membership from "./pages/Membership";
import GroupTripPlanner from "./pages/GroupTripPlanner";
import TripExpenseManagerPage from "./pages/TripExpenseManagerPage";
import FamilyExpenseHubPage from "./pages/FamilyExpenseHubPage";
import MySocialPulse from "./pages/MySocialPulse";
import MySocialPulseArticle from "./pages/MySocialPulseArticle";
import MSPLanding from "./pages/MSPLanding";
import MspTrips from "./pages/msp/MspTrips";
import MspPlan from "./pages/msp/MspPlan";
import MspHotels from "./pages/msp/MspHotels";
import MspFlights from "./pages/msp/MspFlights";
import MspMaps from "./pages/msp/MspMaps";
import MspDocuments from "./pages/msp/MspDocuments";
import MspExpenses from "./pages/msp/MspExpenses";
import MspChecklists from "./pages/msp/MspChecklists";
import MspAssistant from "./pages/msp/MspAssistant";
import TravelGuardian from "./pages/TravelGuardian";
import GuardianAccept from "./pages/GuardianAccept";
import GuardianStory from "./pages/GuardianStory";
import FoodShopping from "./pages/FoodShopping";
import PantryPage from "./pages/PantryPage";
import AdminPulseSources from "./pages/AdminPulseSources";
import DocumentsLocker from "./pages/DocumentsLocker";
import FamilyAccept from "./pages/FamilyAccept";
import TravelHubLanding from "./pages/TravelHubLanding";
import TrustedCircle from "./pages/TrustedCircle";
import NotificationPreferences from "./pages/NotificationPreferences";
import DevicesDashboard from "./pages/DevicesDashboard";
import GroupOnboarding from "./pages/GroupOnboarding";
import PilotDashboard from "./pages/PilotDashboard";
import OwnershipTransfer from "./pages/OwnershipTransfer";
import OwnershipAccept from "./pages/OwnershipAccept";
import Download from "./pages/Download";
import AppStart from "./pages/AppStart";
import SharedExpenses from "./pages/SharedExpenses";
import EmbedGallery from "./pages/EmbedGallery";
import CountdownWidget from "./pages/CountdownWidget";
import SupportUs from "./pages/SupportUs";
import Feedback from "./pages/Feedback";
import HouseholdAccept from "./pages/HouseholdAccept";
import { PaymentSuccess, PaymentCancel } from "./pages/PaymentResult";
import MapExplorer from "./pages/MapExplorer";
import Aircraft from "./pages/Aircraft";
import AircraftDetail from "./pages/AircraftDetail";
import Manufacturers from "./pages/Manufacturers";
import ManufacturerDetail from "./pages/ManufacturerDetail";
import Articles from "./pages/Articles";
import ArticleDetail from "./pages/ArticleDetail";
import LoyaltyExplore from "./pages/LoyaltyExplore";
import LoyaltyCatalog from "./pages/LoyaltyCatalog";
import Hotels from "./pages/Hotels";
import HotelsPulse from "./pages/HotelsPulse";
import HotelsPulseBrowse from "./pages/HotelsPulseBrowse";
import HotelsPulseProperty from "./pages/HotelsPulseProperty";
import HotelsPulseDashboard from "./pages/HotelsPulseDashboard";
import HotelsPulseQuote from "./pages/HotelsPulseQuote";
import AdminAffiliates from "./pages/AdminAffiliates";
import AdminDestinations from "./pages/AdminDestinations";
import AdminCoupons from "./pages/AdminCoupons";
import AdminFares from "./pages/AdminFares";
import AdminRefTraffic from "./pages/AdminRefTraffic";
import RefTracker from "./components/RefTracker";
import Features from "./pages/Features";
import AdminAnswers from "./pages/AdminAnswers";
import AdminVisaRequirements from "./pages/AdminVisaRequirements";
import Faq from "./pages/Faq";
import PackingChecklist from "./pages/PackingChecklist";
import FlightSearch from "./pages/FlightSearch";
import BaggageFees from "./pages/BaggageFees";
import PetTravel from "./pages/PetTravel";
import FareComparison from "./pages/FareComparison";
import AdminFeedback from "./pages/AdminFeedback";
import AdminConsent from "./pages/AdminConsent";
import AdminListen from "./pages/AdminListen";
import Wallet from "./pages/Wallet";
import Advertise from "./pages/Advertise";
import Press from "./pages/Press";
import AdminAdvertising from "./pages/AdminAdvertising";
import NotFound from "./pages/NotFound";
import ContactPage from "./pages/ContactPage";
import Community from "./pages/Community";
import CommunityPost from "./pages/CommunityPost";
import SavedCommunityPosts from "./pages/SavedCommunityPosts";
import Memoirs from "./pages/Memoirs";
import SharedMemoir from "./pages/SharedMemoir";
import TravelDiariesList from "./pages/TravelDiariesList";
import FoodDining from "./pages/FoodDining";
import SharedFoodList from "./pages/SharedFoodList";
import StayGuide from "./pages/StayGuide";
import BusinessSubmit from "./pages/BusinessSubmit";
import AdminIntelligence from "./pages/AdminIntelligence";
import OfficialsApply from "./pages/OfficialsApply";
import AdminOfficials from "./pages/AdminOfficials";
import Complaints from "./pages/Complaints";
import FlightRecovery from "./pages/FlightRecovery";
import PriceWatch from "./pages/PriceWatch";
import AdminTravelCommerce from "./pages/AdminTravelCommerce";
import ExploreGuide from "./pages/ExploreGuide";
import ExplorePlaces from "./pages/ExplorePlaces";
import TopDestinations from "./pages/TopDestinations";
import TravelNews from "./pages/TravelNews";
import MyDealAlerts from "./pages/MyDealAlerts";
import IndiaCities from "./pages/IndiaCities";
import UsCities from "./pages/UsCities";
import UkCities from "./pages/UkCities";
import UaeCities from "./pages/UaeCities";
import AuCities from "./pages/AuCities";
import WorldCities from "./pages/WorldCities";
import CityGuidesHub from "./pages/CityGuidesHub";
import AppLogin from "./pages/AppLogin";
import SocialPulseCallback from "./pages/SocialPulseCallback";
import SsoComplete from "./pages/SsoComplete";
import SsoError from "./pages/SsoError";
import OfflineHub from "./pages/OfflineHub";
import DeleteAccount from "./pages/DeleteAccount";
import MobileAuthHandshake from "./pages/MobileAuthHandshake";
import AdminGeoContent from "./pages/AdminGeoContent";
import ComplaintDetail from "./pages/ComplaintDetail";
import NewComplaint from "./pages/NewComplaint";
import Login from "./pages/Auth";
import Register from "./pages/Auth";
import AuthComplete from "./pages/AuthComplete";
import ProfileSettings from "./pages/ProfileSettings";
import PrivacySecurity from "./pages/PrivacySecurity";
import SecurityPrivacy from "./pages/SecurityPrivacy";
import MyTravelHubGuide from "./pages/MyTravelHubGuide";
import SharedDoc from "./pages/SharedDoc";
import { StepUpProvider } from "./components/StepUpGuard";
import { MfaGate } from "./components/MfaGate";
import Dashboard from "./pages/Dashboard";
import Moderation from "./pages/Moderation";

// Scroll to the top of the page on every route change (footer links, in-page links, etc.)
// If the URL contains a #hash, scroll to that element instead (retry briefly while content loads).
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return; }
    const id = hash.slice(1);
    let tries = 0;
    const attempt = () => {
      const el = document.getElementById(id);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); return; }
      if (tries++ < 20) setTimeout(attempt, 150);
    };
    attempt();
  }, [pathname, hash]);
  return null;
}

function Layout() {
  const { pathname, search } = useLocation();
  const travelDayMode = pathname.startsWith("/travel-day/"); // distraction-free screen: no site chrome
  // HotelsPulse direct-quote links: standalone brand experience, no TravelerPulse chrome
  const quoteMode = pathname.startsWith("/quote/");
  // Embeddable pages (partner sites iframe them with ?embed=1): hide all chrome
  const embedMode = (pathname.startsWith("/trip-expenses/shared/") || pathname.startsWith("/widgets/"))
    && new URLSearchParams(search).get("embed") === "1";
  // Native App Preview: `?native=1` locks the viewport to a phone frame and
  // hides desktop chrome so the preview looks like the installed app. Sticky
  // for the browser tab via sessionStorage so internal links keep the mode.
  const searchParams = new URLSearchParams(search);
  if (searchParams.get("native") === "1") {
    try { sessionStorage.setItem("tp_native_preview", "1"); } catch { /* silent */ }
  } else if (searchParams.get("native") === "0") {
    try { sessionStorage.removeItem("tp_native_preview"); } catch { /* silent */ }
  }
  let nativeMode = false;
  try { nativeMode = sessionStorage.getItem("tp_native_preview") === "1"; } catch { nativeMode = false; }
  useEffect(() => {
    if (nativeMode) document.documentElement.classList.add("tp-native-preview");
    else document.documentElement.classList.remove("tp-native-preview");
  }, [nativeMode]);
  const exitNative = () => {
    try { sessionStorage.removeItem("tp_native_preview"); } catch { /* silent */ }
    window.location.assign("/?native=0");
  };
  const bare = travelDayMode || embedMode || quoteMode || nativeMode;
  // My Social Pulse app-shell: hide website chrome (location strip, footer, install promo)
  const pulseMode = pathname.startsWith("/my-social-pulse") || pathname.startsWith("/locker") || pathname.startsWith("/msp") || pathname.startsWith("/app");
  // /msp/* and /app/* inner app pages render their own MspShell top bar
  // and bottom nav, so hide site Navbar/BottomNav/AskAI too. /msp landing keeps site nav.
  const mspAppShell = pathname.startsWith("/msp/") || pathname.startsWith("/app/");
  // IndianAirports.com tenant: renders its OWN header/nav/footer (IAShell) — hide TP chrome
  const iaMode = pathname.startsWith("/india/");
  // HotelsPulse.pro tenant: renders its OWN HPHeader + brand — hide TP chrome
  const hpMode = pathname.startsWith("/hotelspulse");
  return (
    <div className="App flex flex-col min-h-screen">
      <ScrollToTop />
      {!bare && !mspAppShell && <SocialPulseRibbon />}
      {!bare && !iaMode && !hpMode && !mspAppShell && <Navbar />}
      {!bare && !pulseMode && !iaMode && !hpMode && <LocationBar />}
      {/* pb-16 on mobile so content is never hidden behind the PWA bottom tab bar */}
      <main className={`flex-1 ${bare || mspAppShell ? "" : "pb-16 md:pb-0"}`}>
        <Outlet />
        {!bare && !pulseMode && !iaMode && !hpMode && <WikipediaCard />}
      </main>
      {!bare && !pulseMode && !iaMode && !hpMode && <Footer />}
      {!bare && !hpMode && !mspAppShell && <AskTravelPulse />}
      {!bare && !iaMode && !hpMode && !mspAppShell && <MobileBottomNav />}
      {!bare && !pulseMode && !iaMode && !hpMode && <InstallPrompt />}
      {!bare && <ConsentRePrompt />}
      {!bare && !mspAppShell && <SsoRecoveryBanner />}
      {nativeMode && (
        <button onClick={exitNative} data-testid="native-preview-tag"
          className="fixed bottom-4 right-4 z-[60] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/95 text-primary-foreground text-[11px] font-700 tracking-wide shadow-warm-md backdrop-blur-sm hover:opacity-95">
          App preview · tap to exit
        </button>
      )}
    </div>
  );
}

function AppRouter() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/air" element={<CategoryHub category="air" />} />
        <Route path="/rail" element={<Rail />} />
        <Route path="/rail/stations/:id" element={<RailStation />} />
        <Route path="/rail/operators/:id" element={<RailOperator />} />
        <Route path="/bus" element={<TransportVertical mode="bus" />} />
        <Route path="/ground" element={<TransportVertical mode="ground" />} />
        <Route path="/rideshare" element={<TransportVertical mode="rideshare" />} />
        <Route path="/road" element={<TransportVertical mode="road" />} />
        <Route path="/water" element={<TransportVertical mode="water" />} />
        <Route path="/stays" element={<TransportVertical mode="stay" />} />
        <Route path="/t/:mode/hubs/:id" element={<TransportHubPage />} />
        <Route path="/t/:mode/operators/:id" element={<TransportOperatorPage />} />
        <Route path="/visas" element={<VisasHub />} />
        <Route path="/visas/missions/:id" element={<MissionProfile />} />
        <Route path="/visas/regions/:host/:region" element={<RegionMissions />} />
        <Route path="/visas/:id" element={<VisaCountryDetail />} />
        <Route path="/about" element={<About />} />
        <Route path="/legal/affiliate-disclosure" element={<AffiliateDisclosure />} />
        <Route path="/legal/affiliate-disclosures" element={<AffiliateDisclosure />} />
        {/* iter216 — Private Vault is now local-first (IndexedDB + Web Crypto).
            /vault/legacy still points to the old cloud-backed SafeVault; that
            route is preserved so users can screenshot/migrate any lingering
            data before we retire it. */}
        <Route path="/vault" element={<PrivateVault />} />
        <Route path="/vault/legacy" element={<SafeVault />} />
        <Route path="/vault/health" element={<PrivateVault />} />
        <Route path="/app/vault" element={<PrivateVault />} />
        <Route path="/msp/vault" element={<Navigate to="/app/vault" replace />} />
        <Route path="/emergency/:token" element={<PublicEmergencyCard />} />
        <Route path="/features" element={<Features />} />
        <Route path="/about/founder" element={<AboutFounder />} />
        <Route path="/explore-plan-share-travel" element={<OurStory />} />
        <Route path="/guides" element={<GuidesIndex />} />
        <Route path="/articles" element={<ArticlesIndex />} />
        <Route path="/articles/special/our-cochin" element={<OurCochin />} />
        <Route path="/articles/special/the-chenda" element={<TheChenda />} />
        <Route path="/articles/special/kerala-ayurveda" element={<KeralaAyurveda />} />
        <Route path="/specials" element={<Specials />} />
        <Route path="/articles/:slug" element={<ArticleView />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/business/:id" element={<BusinessDetail />} />
        <Route path="/my-business" element={<MyBusiness />} />
        <Route path="/guides/:mode" element={<SectionGuide />} />
        <Route path="/destinations" element={<Destinations />} />
        <Route path="/destinations/:id" element={<DestinationDetail />} />
        <Route path="/airports" element={<Airports />} />
        <Route path="/airports/directory" element={<AirportsDirectory />} />
        <Route path="/airports/ranking" element={<GlobalAirportRanking />} />
        <Route path="/airports/:code" element={<AirportDetail />} />
        <Route path="/airlines" element={<Airlines />} />
        <Route path="/airlines/directory" element={<AirlinesDirectory />} />
        <Route path="/airlines/ranking" element={<GlobalAirlineRanking />} />
        <Route path="/airlines/:code" element={<AirlineDetail />} />
        <Route path="/countries" element={<Countries />} />
        <Route path="/countries/:code" element={<CountryDetail />} />
        <Route path="/regions/:code" element={<StateDetail />} />
        <Route path="/legal/:slug" element={<Legal />} />
        <Route path="/cities/:name" element={<City />} />
        <Route path="/map" element={<MapExplorer />} />
        <Route path="/aircraft" element={<Aircraft />} />
        <Route path="/aircraft/:code" element={<AircraftDetail />} />
        <Route path="/manufacturers" element={<Manufacturers />} />
        <Route path="/manufacturers/:name" element={<ManufacturerDetail />} />
        <Route path="/news" element={<Articles />} />
        <Route path="/news/:slug" element={<ArticleDetail />} />
        <Route path="/loyalty" element={<LoyaltyExplore />} />
        <Route path="/loyalty/catalog" element={<LoyaltyCatalog />} />
        <Route path="/loyalty/programs" element={<LoyaltyCatalog />} />
        <Route path="/hotels" element={<Hotels />} />
        <Route path="/hotelspulse" element={<HotelsPulse />} />
        <Route path="/hotelspulse/stays" element={<HotelsPulseBrowse />} />
        <Route path="/hotelspulse/stay/:slug" element={<HotelsPulseProperty />} />
        <Route path="/hotelspulse/dashboard" element={<ProtectedRoute><HotelsPulseDashboard /></ProtectedRoute>} />
        <Route path="/quote/:token" element={<HotelsPulseQuote />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/community" element={<Community />} />
        <Route path="/community/post/:pid" element={<CommunityPost />} />
        <Route path="/community/saved" element={<ProtectedRoute><SavedCommunityPosts /></ProtectedRoute>} />
        <Route path="/memoirs" element={<ProtectedRoute><Memoirs /></ProtectedRoute>} />
        <Route path="/memoirs/shared/:token" element={<SharedMemoir />} />
        <Route path="/travel-diaries" element={<ProtectedRoute><TravelDiariesList /></ProtectedRoute>} />
        <Route path="/officials/apply" element={<ProtectedRoute><OfficialsApply /></ProtectedRoute>} />
        <Route path="/admin/officials" element={<ProtectedRoute moderator><AdminOfficials /></ProtectedRoute>} />
        <Route path="/admin/affiliates" element={<ProtectedRoute moderator><AdminAffiliates /></ProtectedRoute>} />
        <Route path="/admin/destinations" element={<ProtectedRoute moderator><AdminDestinations /></ProtectedRoute>} />
        <Route path="/admin/travel" element={<ProtectedRoute moderator><AdminTravelCommerce /></ProtectedRoute>} />
        <Route path="/admin/geo" element={<ProtectedRoute moderator><AdminGeoContent /></ProtectedRoute>} />
        <Route path="/admin/coupons" element={<ProtectedRoute moderator><AdminCoupons /></ProtectedRoute>} />
        <Route path="/admin/fares" element={<ProtectedRoute moderator><AdminFares /></ProtectedRoute>} />
        <Route path="/admin/ref-traffic" element={<ProtectedRoute moderator><AdminRefTraffic /></ProtectedRoute>} />
        <Route path="/admin/answers" element={<ProtectedRoute moderator><AdminAnswers /></ProtectedRoute>} />
        <Route path="/admin/visa-requirements" element={<ProtectedRoute moderator><AdminVisaRequirements /></ProtectedRoute>} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/packing" element={<PackingChecklist />} />
        <Route path="/flight-search" element={<FlightSearch />} />
        <Route path="/baggage" element={<BaggageFees />} />
        <Route path="/pet-travel" element={<PetTravel />} />
        <Route path="/fares" element={<FareComparison />} />
        <Route path="/admin/feedback" element={<ProtectedRoute moderator><AdminFeedback /></ProtectedRoute>} />
        <Route path="/admin/consent" element={<ProtectedRoute moderator><AdminConsent /></ProtectedRoute>} />
        <Route path="/admin/listen" element={<ProtectedRoute moderator><AdminListen /></ProtectedRoute>} />
        <Route path="/wallet" element={<Wallet />} />
        <Route path="/advertise" element={<Advertise />} />
        <Route path="/press" element={<Press />} />
        <Route path="/admin/advertising" element={<ProtectedRoute moderator><AdminAdvertising /></ProtectedRoute>} />
        <Route path="/complaints" element={<Complaints />} />
        <Route path="/recovery" element={<FlightRecovery />} />
        <Route path="/price-watch" element={<PriceWatch />} />
        <Route path="/food-dining/*" element={<FoodDining />} />
        <Route path="/food-list/shared/:token" element={<SharedFoodList />} />
        <Route path="/hotels-accommodation/*" element={<StayGuide />} />
        <Route path="/submit-business" element={<BusinessSubmit />} />
        <Route path="/admin/intelligence" element={<ProtectedRoute moderator><AdminIntelligence /></ProtectedRoute>} />
        <Route path="/explore/*" element={<ExploreGuide />} />
        <Route path="/explore-places" element={<ExplorePlaces />} />
        <Route path="/top-destinations" element={<TopDestinations />} />
        <Route path="/travel-news" element={<TravelNews />} />
        <Route path="/my-deal-alerts" element={<ProtectedRoute><MyDealAlerts /></ProtectedRoute>} />
        <Route path="/india-cities" element={<IndiaCities />} />
        <Route path="/us-cities" element={<UsCities />} />
        <Route path="/uk-cities" element={<UkCities />} />
        <Route path="/uae-cities" element={<UaeCities />} />
        <Route path="/au-cities" element={<AuCities />} />
        <Route path="/world-cities/:key" element={<WorldCities />} />
        <Route path="/city-guides" element={<CityGuidesHub />} />
        <Route path="/complaints/new" element={<ProtectedRoute><NewComplaint /></ProtectedRoute>} />
        <Route path="/complaints/:cid" element={<ComplaintDetail />} />
        <Route path="/login" element={<Login />} />
        <Route path="/app-login" element={<AppLogin />} />
        <Route path="/auth/mobile" element={<MobileAuthHandshake />} />
        <Route path="/auth/socialpulse-callback" element={<SocialPulseCallback />} />
        <Route path="/auth/sso-complete" element={<SsoComplete />} />
        <Route path="/auth/sso-error" element={<SsoError />} />
        <Route path="/offline" element={<OfflineHub />} />
        <Route path="/auth/callback" element={<SocialPulseCallback />} />
        <Route path="/delete-account" element={<DeleteAccount />} />
        <Route path="/register" element={<Register />} />
        <Route path="/auth/complete" element={<AuthComplete />} />
        <Route path="/profile" element={<ProtectedRoute><ProfileSettings /></ProtectedRoute>} />
        <Route path="/privacy-security" element={<ProtectedRoute><PrivacySecurity /></ProtectedRoute>} />
        <Route path="/security-privacy" element={<SecurityPrivacy />} />
        <Route path="/my-travel-hub-guide" element={<MyTravelHubGuide />} />
        <Route path="/shared-doc/:token" element={<SharedDoc />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/travel-hub" element={<TravelHub />} />
        <Route path="/guardian" element={<ProtectedRoute><TravelGuardian /></ProtectedRoute>} />
        <Route path="/guardian/accept" element={<GuardianAccept />} />
        <Route path="/guardian/story/:token" element={<GuardianStory />} />
        <Route path="/grouptrip-planner" element={<GroupTripPlanner />} />
        <Route path="/trip-expense-manager" element={<TripExpenseManagerPage />} />
        <Route path="/family-expense-hub" element={<FamilyExpenseHubPage />} />
        <Route path="/my-social-pulse" element={<ProtectedRoute><MySocialPulse /></ProtectedRoute>} />
        <Route path="/my-social-pulse/food" element={<ProtectedRoute><FoodShopping /></ProtectedRoute>} />
        <Route path="/my-social-pulse/pantry" element={<ProtectedRoute><PantryPage /></ProtectedRoute>} />
        <Route path="/admin/pulse-sources" element={<ProtectedRoute moderator><AdminPulseSources /></ProtectedRoute>} />
        <Route path="/locker" element={<ProtectedRoute><DocumentsLocker /></ProtectedRoute>} />
        <Route path="/family/accept" element={<FamilyAccept />} />
        <Route path="/travel-hub-landing" element={<TravelHubLanding />} />
        <Route path="/trusted-circle" element={<ProtectedRoute><TrustedCircle /></ProtectedRoute>} />
        <Route path="/inner-circle" element={<ProtectedRoute><TrustedCircle /></ProtectedRoute>} />
        <Route path="/settings/notifications" element={<ProtectedRoute><NotificationPreferences /></ProtectedRoute>} />
        <Route path="/devices" element={<ProtectedRoute><DevicesDashboard /></ProtectedRoute>} />
        <Route path="/group-onboarding" element={<ProtectedRoute><GroupOnboarding /></ProtectedRoute>} />
        <Route path="/pilot" element={<ProtectedRoute><PilotDashboard /></ProtectedRoute>} />
        <Route path="/settings/ownership-transfer" element={<ProtectedRoute><OwnershipTransfer /></ProtectedRoute>} />
        <Route path="/settings/ownership-transfer/accept" element={<OwnershipAccept />} />
        <Route path="/download" element={<Download />} />
        <Route path="/install" element={<Download />} />
        <Route path="/settings/privacy" element={<ProtectedRoute><PrivacySecurity /></ProtectedRoute>} />
        <Route path="/app-start" element={<AppStart />} />
        <Route path="/trip-expenses/shared/:token" element={<SharedExpenses />} />
        <Route path="/embed" element={<EmbedGallery />} />
        <Route path="/widgets/countdown/:token" element={<CountdownWidget />} />
        <Route path="/widgets/india-planner" element={<IndiaPlannerWidget />} />
        <Route path="/plan-ai" element={<TripPlannerAI />} />
        <Route path="/trip-today" element={<TripToday />} />
        <Route path="/get-app" element={<GetApp />} />
        <Route path="/social-pulse-app" element={<MySocialPulseArticle />} />
        <Route path="/msp" element={<Navigate to="/app" replace />} />
        <Route path="/app" element={<MSPLanding />} />
        <Route path="/msp/trips" element={<Navigate to="/app/trips" replace />} />
        <Route path="/app/trips" element={<MspTrips />} />
        <Route path="/msp/plan" element={<Navigate to="/app/plan" replace />} />
        <Route path="/app/plan" element={<MspPlan />} />
        <Route path="/msp/hotels" element={<Navigate to="/app/hotels" replace />} />
        <Route path="/app/hotels" element={<MspHotels />} />
        <Route path="/msp/flights" element={<Navigate to="/app/flights" replace />} />
        <Route path="/app/flights" element={<MspFlights />} />
        <Route path="/msp/maps" element={<Navigate to="/app/maps" replace />} />
        <Route path="/app/maps" element={<MspMaps />} />
        <Route path="/msp/documents" element={<Navigate to="/app/documents" replace />} />
        <Route path="/app/documents" element={<MspDocuments />} />
        <Route path="/msp/expenses" element={<Navigate to="/app/expenses" replace />} />
        <Route path="/app/expenses" element={<MspExpenses />} />
        <Route path="/msp/checklists" element={<Navigate to="/app/checklists" replace />} />
        <Route path="/app/checklists" element={<MspChecklists />} />
        <Route path="/msp/ai-assistant" element={<Navigate to="/app/ai-assistant" replace />} />
        <Route path="/app/ai-assistant" element={<MspAssistant />} />
        <Route element={<IALayout />}>
          <Route path="/india/airports" element={<IndiaAirportsHome />} />
          <Route path="/india/about" element={<IndiaAbout />} />
          <Route path="/india/my-flight" element={<IndiaMyFlight />} />
          <Route path="/india/guides" element={<IndiaGuidesIndex />} />
          <Route path="/india/guides/oci-nri-rules" element={<IndiaOciGuide />} />
          <Route path="/india/guides/visiting-india" element={<IndiaVisitingGuide />} />
          <Route path="/india/partners" element={<IndiaPartners />} />
          <Route path="/india/airports/directory" element={<IndiaAirports />} />
          <Route path="/india/airports/routes/:pair" element={<IndiaRoutePage />} />
          <Route path="/india/airports/states/:slug" element={<IndiaStatePage />} />
          <Route path="/india/airports/:slug" element={<IndiaAirportDetail />} />
          <Route path="/india/airlines" element={<IndiaAirlines />} />
          <Route path="/india/airlines/:slug" element={<IndiaAirlineDetail />} />
        </Route>
        <Route path="/remind-me" element={<RemindMe />} />
        <Route path="/gear" element={<GearInventory />} />
        <Route path="/membership" element={<Membership />} />
        <Route path="/MyTravelHub" element={<Membership />} />
        <Route path="/mytravelhub" element={<Membership />} />
        <Route path="/support" element={<SupportUs />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/household/accept" element={<HouseholdAccept />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/trips/:id" element={<ProtectedRoute><TripDetail /></ProtectedRoute>} />
        <Route path="/trips/:id/journey" element={<ProtectedRoute><TripJourney /></ProtectedRoute>} />
        <Route path="/trip/:id/journey" element={<ProtectedRoute><TripJourney /></ProtectedRoute>} />
        <Route path="/follow/:token" element={<FollowPage />} />
        <Route path="/b/:token" element={<PublicBag />}  />
        <Route path="/groups" element={<ProtectedRoute><TravelGroups /></ProtectedRoute>} />
        <Route path="/groups/join" element={<GroupJoin />} />
        <Route path="/groups/:id" element={<ProtectedRoute><GroupDetail /></ProtectedRoute>} />
        <Route path="/groups/:id/guardian" element={<ProtectedRoute><GroupGuardian /></ProtectedRoute>} />
        <Route path="/groups/guardian/shared/:token" element={<SharedGuardian />} />
        <Route path="/expenses" element={<ProtectedRoute><ExpenseManager /></ProtectedRoute>} />
        <Route path="/trips/:id/diary" element={<ProtectedRoute><TripDiary /></ProtectedRoute>} />
        <Route path="/trips/:id/create-story" element={<ProtectedRoute><StoryWizard /></ProtectedRoute>} />
        <Route path="/stories/:sid/edit" element={<ProtectedRoute><StoryStudio /></ProtectedRoute>} />
        <Route path="/my-stories" element={<ProtectedRoute><MyStories /></ProtectedRoute>} />
        <Route path="/story/:publicId" element={<PublicStory />} />
        <Route path="/photo-map" element={<ProtectedRoute><PhotoMap /></ProtectedRoute>} />
        <Route path="/my-photos" element={<ProtectedRoute><MyPhotos /></ProtectedRoute>} />
        <Route path="/travel-day/:id" element={<ProtectedRoute><TravelDay /></ProtectedRoute>} />
        <Route path="/trip-share/:token" element={<TripShareView />} />
        <Route path="/memories/:token" element={<Memories />} />
        <Route path="/moderation" element={<ProtectedRoute moderator><Moderation /></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RefTracker />
        <StepUpProvider>
          <Toaster position="bottom-right" richColors offset={110} mobileOffset={80} />
          <MfaGate />
          <AppRouter />
        </StepUpProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
